import React from "react";
import {observer} from "mobx-react-lite";
import GameStore from "../../store/game";
import BonusStore from "../../store/bonus";
import {LettersRerollHintPrice, TimeBoostHintPrice} from "../../model/prices";

const hintsConfig = {
    time_boost: {
        title: "Time Boost",
        description: "+20 seconds to timer",
    },
    letters_reroll: {
        title: "Letter Shuffle",
        description: "Refresh letters set",
    },
};

const hintPrices = {
    time_boost: TimeBoostHintPrice,
    letters_reroll: LettersRerollHintPrice,
};

// Панель подсказок с балансами и лимитами
export const HintPanel = observer(({onUseHint, onBuyHint}) => {
    const wallet = GameStore.hintWallet || {};
    const limits = GameStore.hintLimits || {};
    const isGameActive = GameStore.isActive;
    const playerBalances = GameStore.playerHintBalances || {};
    const isPlayerHintBalancesLoaded = GameStore.isPlayerHintBalancesLoaded;

    const getGameRemaining = (type) => {
        const limitObj = limits[type] || {};
        const maxLimit = Number(limitObj?.max ?? limitObj?.total ?? limitObj?.limit);
        const usedRaw = Number(limitObj?.used ?? limitObj?.spent);
        if (Number.isFinite(maxLimit)) {
            const used = Number.isFinite(usedRaw) && usedRaw > 0 ? usedRaw : 0;
            const remaining = Math.max(maxLimit - used, 0);
            return {remaining, max: maxLimit, used};
        }

        const available = Number(wallet[type]) || 0;
        const remaining = available < 0 ? 0 : available;
        return {remaining, max: remaining, used: 0};
    };

    const renderHintButton = (type) => {
        const config = hintsConfig[type] || {title: "Hint", description: "Available option"};
        const {remaining, max} = getGameRemaining(type);
        const playerBalance = Number(playerBalances[type]) || 0;
        const isUseAvailable = isGameActive && remaining > 0 && playerBalance > 0;
        const badgeColor = playerBalance > 0 ? "bg-green-600 text-white" : "bg-gray-600 text-gray-200";
        const limitReached = max !== undefined && Number.isFinite(max) && remaining === 0 && max > 0;
        const buyCost = Number(hintPrices[type] ?? 0);
        const canBuyAndUse = isPlayerHintBalancesLoaded
            && isGameActive
            && remaining > 0
            && playerBalance === 0
            && typeof onBuyHint === "function"
            && typeof onUseHint === "function"
            && buyCost > 0;
        const hasEnoughCoins = BonusStore.coins >= buyCost;
        const canAffordBuy = canBuyAndUse && hasEnoughCoins;
        const cardClickable = (isUseAvailable || canAffordBuy) && typeof onUseHint === "function";
        // Покупаем подсказку и применяем её сразу после успешной покупки.
        const handleBuyAndUse = () => {
            if (!canAffordBuy) {
                return;
            }
            const result = onBuyHint?.(type, {skipBalanceUpdate: true});
            if (result && typeof result.then === "function") {
                // Вызываем использование только после успешной покупки.
                result.then(() => onUseHint?.(type)).catch(() => {});
                return;
            }
            onUseHint?.(type);
        };
        const handleUse = () => {
            if (isUseAvailable) {
                onUseHint?.(type);
                return;
            }
            if (canBuyAndUse) {
                handleBuyAndUse();
            }
        };

        return (
            <div
                key={type}
                role="button"
                tabIndex={cardClickable ? 0 : -1}
                onClick={() => cardClickable && handleUse()}
                className={`bg-gray-800 rounded-xl p-3 flex flex-col text-left transition ${cardClickable ? "cursor-pointer hover:bg-gray-700 focus:outline-none" : "opacity-80"}`}
            >
                {/* Значок вынесен в строку заголовка, чтобы не перекрывать текст на маленьких экранах. */}
                <div className="flex items-start justify-between gap-2">
                    <span className="text-sm font-semibold text-gray-100">{config.title}</span>
                    <span className={`shrink-0 px-2 py-0.5 text-[11px] rounded-full ${badgeColor}`}>
                        {playerBalance}
                    </span>
                </div>
                <span className="text-xs text-gray-400 mt-1">{config.description}</span>
                {limitReached && (
                    <div className="mt-3 flex flex-col gap-2">
                        <span className="text-xs font-semibold text-red-300">In-game limit reached</span>
                    </div>
                )}
                {canBuyAndUse && (
                    <div className="mt-3 flex flex-col gap-2">
                        <span className="text-xs font-semibold text-yellow-300">
                            Use for {buyCost} coins
                        </span>
                        {!hasEnoughCoins && (
                            <span className="text-[11px] text-gray-400">Not enough coins</span>
                        )}
                    </div>
                )}
            </div>
        );
    };

    // Фиксируем порядок подсказок по конфигу, даже если кошелек пришел иначе.
    const hintOrder = Object.keys(hintsConfig);
    const walletTypes = Object.keys(wallet || {}).filter((type) => hintsConfig[type]);
    const hintTypes = walletTypes.length > 0
        ? hintOrder.filter((type) => walletTypes.includes(type))
        : hintOrder;

    return (
        <div className="px-4 grid grid-cols-2 gap-2">
            {hintTypes.map(renderHintButton)}
        </div>
    );
});
